import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';
import {
  CheckCircle, Circle, ChevronDown, ChevronUp, Trophy,
  Zap, X, HelpCircle, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  create: 'Create',
  earn: 'Earn',
  discover: 'Discover',
  engage: 'Engage',
  grow: 'Grow',
};

const GUIDES = {
  setup_stripe: {
    title: 'How to Set Up Stripe',
    steps: [
      'Go to your Dashboard and find the "Stripe Connect" card.',
      'Click "Connect with Stripe" to start the setup.',
      'You\'ll be redirected to Stripe — fill in your personal/business info.',
      'Add your bank account or debit card for payouts.',
      'Verify your identity (government ID may be required).',
      'Once approved, Stripe will show "Connected" on your dashboard.',
      'You\'re now ready to receive payments from track sales, reviews, and more!',
    ],
  },
  become_reviewer: {
    title: 'How to Become a Music Reviewer',
    steps: [
      'You need an active Stripe account first (to receive payment for reviews).',
      'Go to the Reviewer page from the sidebar.',
      'Click "Apply to Become a Reviewer" and fill out the application.',
      'Describe your music expertise and listening experience.',
      'An admin will review and approve your application.',
      'Once approved, you can create Review Sessions and set your price per review.',
      'Artists submit tracks to your queue — you listen, rate, and get paid!',
    ],
  },
  ai_verification: {
    title: 'How to Pass AI Verification',
    steps: [
      'AI Verification lets you sell AI-generated tracks on the platform.',
      'Go to your Profile Settings and find "AI Artist Verification".',
      'Provide a link to your Suno, Udio, or other AI music platform profile.',
      'Upload a sample AI-generated track for review.',
      'Describe how you use AI tools in your creative process.',
      'Submit your application — an admin will review it.',
      'Once verified, you\'ll get the "AI Verified" badge and can sell AI tracks.',
      'All future AI track uploads will bypass the automatic AI detection block.',
    ],
  },
};

export default function EngagementChecklist() {
  const { token } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const isLight = mode === 'light';
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [guideOpen, setGuideOpen] = useState(null);

  useEffect(() => {
    if (!token) return;
    const wasDismissed = localStorage.getItem('ov_checklist_dismissed');
    if (wasDismissed === 'true') { setDismissed(true); return; }
    axios.get(`${API}/engagement/checklist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {});
  }, [token]);

  if (dismissed || !data || data.progress_pct === 100) return null;

  const incomplete = data.items.filter(i => !i.completed);
  const completed = data.items.filter(i => i.completed);
  const shown = expanded ? data.items : incomplete.slice(0, 4);

  const dismiss = () => {
    localStorage.setItem('ov_checklist_dismissed', 'true');
    setDismissed(true);
  };

  const cardBg = isLight ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#0d0d18] border-white/[0.06]';
  const textPrimary = isLight ? 'text-gray-900' : 'text-white';
  const textMuted = isLight ? 'text-gray-500' : 'text-gray-400';

  return (
    <>
      <div className={`border rounded-xl overflow-hidden ${cardBg}`} data-testid="engagement-checklist">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#0077b6]/10' : 'bg-[#00CCFF]/10'}`}>
              <Trophy size={18} className={isLight ? 'text-[#0077b6]' : 'text-[#00CCFF]'} />
            </div>
            <div>
              <p className={`font-['Chakra_Petch'] text-sm uppercase tracking-wide ${textPrimary}`}>
                Get Started
              </p>
              <p className={`font-mono text-[11px] ${textMuted}`}>
                {data.completed}/{data.total} completed &middot; {data.earned_points} pts
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setExpanded(!expanded)} className={`p-1 ${textMuted} hover:${textPrimary}`}>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            <button onClick={dismiss} className={`p-1 ${textMuted}`} title="Dismiss checklist">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className={`mx-4 mb-3 h-1.5 rounded-full ${isLight ? 'bg-gray-100' : 'bg-white/5'}`}>
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#00CCFF] to-[#00ff88] transition-all duration-500"
            style={{ width: `${data.progress_pct}%` }}
          />
        </div>

        {/* Items */}
        <div className="px-4 pb-3 space-y-1">
          {shown.map(item => (
            <div
              key={item.key}
              className={`flex items-center gap-3 py-2 px-2 rounded-lg cursor-pointer transition-colors group ${
                item.completed
                  ? isLight ? 'opacity-50' : 'opacity-40'
                  : isLight ? 'hover:bg-gray-50' : 'hover:bg-white/[0.03]'
              }`}
              onClick={() => !item.completed && navigate(item.link)}
              data-testid={`checklist-item-${item.key}`}
            >
              {item.completed ? (
                <CheckCircle size={16} className="text-[#00ff88] flex-shrink-0" />
              ) : (
                <Circle size={16} className={`flex-shrink-0 ${isLight ? 'text-gray-300' : 'text-white/15'}`} />
              )}
              <span className={`text-sm flex-1 ${item.completed ? 'line-through' : ''} ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
                {item.label}
              </span>
              <span className={`font-mono text-[10px] ${textMuted}`}>+{item.points}</span>
              {item.has_guide && !item.completed && (
                <button
                  onClick={(e) => { e.stopPropagation(); setGuideOpen(item.key); }}
                  className={`p-0.5 ${isLight ? 'text-[#0077b6]' : 'text-[#00CCFF]'} opacity-0 group-hover:opacity-100 transition-opacity`}
                  title="How to"
                >
                  <HelpCircle size={14} />
                </button>
              )}
              {!item.completed && !item.has_guide && (
                <ExternalLink size={12} className={`${textMuted} opacity-0 group-hover:opacity-100 transition-opacity`} />
              )}
            </div>
          ))}
        </div>

        {/* Show all / collapse */}
        {!expanded && incomplete.length > 4 && (
          <button
            onClick={() => setExpanded(true)}
            className={`w-full py-2 font-mono text-xs border-t ${isLight ? 'border-gray-100 text-gray-500 hover:text-gray-700' : 'border-white/[0.04] text-gray-500 hover:text-gray-300'} transition-colors`}
          >
            Show all {data.total} items ({incomplete.length - 4} more)
          </button>
        )}
        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className={`w-full py-2 font-mono text-xs border-t ${isLight ? 'border-gray-100 text-gray-500 hover:text-gray-700' : 'border-white/[0.04] text-gray-500 hover:text-gray-300'} transition-colors`}
          >
            Show less
          </button>
        )}
      </div>

      {/* Guide Modal */}
      {guideOpen && GUIDES[guideOpen] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setGuideOpen(null)}>
          <div
            className={`max-w-md w-full rounded-xl border p-6 ${isLight ? 'bg-white border-gray-200' : 'bg-[#0a0a0a] border-white/[0.08]'}`}
            onClick={e => e.stopPropagation()}
            data-testid={`guide-modal-${guideOpen}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-['Chakra_Petch'] uppercase text-lg ${textPrimary}`}>
                {GUIDES[guideOpen].title}
              </h3>
              <button onClick={() => setGuideOpen(null)} className={textMuted}><X size={18} /></button>
            </div>
            <ol className="space-y-3">
              {GUIDES[guideOpen].steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-mono text-xs font-bold ${
                    isLight ? 'bg-[#0077b6]/10 text-[#0077b6]' : 'bg-[#00CCFF]/10 text-[#00CCFF]'
                  }`}>{i + 1}</span>
                  <p className={`text-sm leading-relaxed ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>{step}</p>
                </li>
              ))}
            </ol>
            <button
              onClick={() => { setGuideOpen(null); navigate(data.items.find(i => i.key === guideOpen)?.link || '/'); }}
              className={`mt-5 w-full py-2.5 rounded-lg font-['Chakra_Petch'] uppercase text-sm tracking-wider ${
                isLight ? 'bg-[#0077b6] text-white' : 'bg-[#00CCFF] text-black'
              }`}
            >
              Let's Go
            </button>
          </div>
        </div>
      )}
    </>
  );
}
